'use client';

import { useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import { useToastStore } from '@/stores/useToastStore';
import style from './toast.module.css';
import { useShallow } from 'zustand/react/shallow';
import { animationClass } from '@/lib/_utils';
import { Icon } from '../icon/Icon';

interface ContentToast {
	text: string | null;
	status: string | null;
	layout: string | null;
	className: string | null;
	fields: string[];
}

function Toast() {
	const [content, setContent] = useState<ContentToast>({
		text: null,
		status: null,
		layout: null,
		className: null,
		fields: [],
	});
	const notificationEl = useRef(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const styleByStatus: any = {
		success: { iconName: 'checkCircleIcon', iconVariant: 'success', color: 'text-success-500' },
		warning: {
			iconName: 'exclamationCircleIcon',
			iconVariant: 'warning',
			color: 'text-warning-500',
		},
		danger: { iconName: 'crossCircleIcon', iconVariant: 'danger', color: 'text-danger-500' },
		loading: { iconName: 'loaderIcon', iconVariant: 'white', color: 'text-white' },
	};

	const toastData = useToastStore(
		useShallow((state) => ({
			toastActived: state.toastActived,
			text: state.text,
			status: state.status,
			layout: state.layout,
			className: state.className,
			fields: state.fields,
			closeToast: state.closeToast,
		})),
	);

	const initContent = () =>
		setContent({
			text: toastData.text,
			status: toastData.status,
			layout: toastData.layout,
			className: toastData.className,
			fields: toastData.fields ?? [],
		});

	function emptyContent() {
		setContent({
			text: null,
			status: null,
			layout: null,
			className: null,
			fields: [],
		});
	}

	const handleCloseClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		toastData.closeToast();
	};

	return (
		<Transition
			nodeRef={notificationEl}
			in={toastData.toastActived}
			mountOnEnter
			unmountOnExit
			onEnter={initContent}
			onExited={emptyContent}
			timeout={{ enter: 200, exit: 200 }}>
			{(state: string) => (
				<div
					className={`${style.main_toast} ${
						content.layout ? style[content.layout] : style['default']
					} ${content.status} ${animationClass(state, style.toast_in, style.toast_out)} ${
						content.status ? style[content.status] : ''
					} ${content?.className}`}>
					<div className={`${style.toast_content}`}>
						<div className={`${style.toast_intro}`}>
							{!!content?.status && (
								<div className={`mr-[8px]`}>
									<Icon
										name={styleByStatus[content?.status]?.iconName}
										variant={styleByStatus[content?.status]?.iconVariant}></Icon>
								</div>
							)}
							<div
								className={`text-[16px] font-semibold leading-[1.5] ${
									content.status ? styleByStatus[content.status].color : ''
								}`}>
								{content.text}
							</div>
						</div>
						{content.fields.length > 0 && (
							<div className={`${style.description}`}>
								<ul>
									{content.fields.map((field, i) => (
										<li key={i} dangerouslySetInnerHTML={{ __html: field }}></li>
									))}
								</ul>
							</div>
						)}
					</div>
					<div className={`${style.toast_close}`} onClick={handleCloseClick}>
						<Icon name='crossIcon'></Icon>
					</div>
				</div>
			)}
		</Transition>
	);
}

export default Toast;
